/**
 * IncubationPage
 *
 * Operational dashboard showing production batches currently incubating.
 * Displays batches in SHOOGLED or INCUBATING state with calculated expected end dates.
 *
 * Allows operators to mark batches as fully colonised when incubation is complete.
 */

import { useState, useMemo } from 'react'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useIncubatingBatches, useIncubationMutations } from '@/hooks/useIncubatingBatches'
import { useSpawnMutations } from '@/hooks/useSpawnBatches'
import { Timer, RefreshCw, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react'
import IncubationEnvironmentCard from '@/components/production/incubation/IncubationEnvironmentCard'

export default function IncubationPage({ user, onLogout }) {
  const { batches, summary, loading, error, refetch } = useIncubatingBatches()
  const { markAsColonised, loading: mutationLoading } = useIncubationMutations()
  const { markAsColonised: markSpawnColonised } = useSpawnMutations()
  const [processingIds, setProcessingIds] = useState(new Set())

  const blockBatches = useMemo(() => batches.filter(b => b.type !== 'spawn'), [batches])
  const spawnBatches = useMemo(() => batches.filter(b => b.type === 'spawn'), [batches])

  const handleMarkColonised = async (batch) => {
    setProcessingIds(prev => new Set(prev).add(batch.id))
    try {
      if (batch.type === 'spawn') {
        await markSpawnColonised(batch.id)
      } else {
        await markAsColonised(batch.id)
      }
      refetch()
    } catch (err) {
      console.error('Failed to mark as colonised:', err)
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(batch.id)
        return next
      })
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const getProgressVariant = (progressPercent, daysRemaining) => {
    if (progressPercent >= 100 || daysRemaining === 0) return 'destructive'
    if (daysRemaining <= 3) return 'warning'
    return 'default'
  }

  if (loading) {
    return (
      <PageLayout
        title="Incubation"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-content-secondary">Loading...</div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Incubation"
      user={user}
      onLogout={onLogout}
      headerActions={
        <Button
          variant="outline"
          size="sm"
          onClick={refetch}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Timer className="h-4 w-4" />
                Total Incubating
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{batches.length}</div>
              <p className="text-sm text-muted-foreground">batches</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Blocks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{summary.total_blocks}</div>
              <p className="text-sm text-muted-foreground">blocks incubating</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Ready Soon
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold">{summary.ready_soon}</span>
                {summary.overdue > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {summary.overdue} overdue
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">within 3 days</p>
            </CardContent>
          </Card>

          <IncubationEnvironmentCard />
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Block Batches Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Substrate Blocks</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {blockBatches.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">
                No substrate blocks incubating
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Batch Code</TableHead>
                    <TableHead>Expected End</TableHead>
                    <TableHead>Strain</TableHead>
                    <TableHead className="text-right">Blocks</TableHead>
                    <TableHead className="text-right">Weight (kg)</TableHead>
                    <TableHead className="w-48">Progress</TableHead>
                    <TableHead className="w-32">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blockBatches.map((batch) => {
                    const isProcessing = processingIds.has(batch.id)
                    const isOverdue = batch.progress_percent >= 100

                    return (
                      <TableRow key={batch.id} className={isOverdue ? 'bg-destructive/5' : ''}>
                        <TableCell>{formatDate(batch.inoculation_date)}</TableCell>
                        <TableCell className="font-medium">{batch.batch_code}</TableCell>
                        <TableCell>
                          <span className={isOverdue ? 'text-destructive font-medium' : ''}>
                            {formatDate(batch.expected_end_date)}
                          </span>
                          {batch.days_remaining > 0 && (
                            <span className="text-muted-foreground text-sm ml-1">
                              ({batch.days_remaining}d)
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{batch.strain_code}</span>
                          <span className="text-muted-foreground ml-1">{batch.strain_name}</span>
                        </TableCell>
                        <TableCell className="text-right">{batch.total_blocks}</TableCell>
                        <TableCell className="text-right">
                          {batch.total_weight_kg ? batch.total_weight_kg.toFixed(1) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress
                              value={Math.min(batch.progress_percent, 100)}
                              className="h-2 flex-1"
                            />
                            <span className="text-xs text-muted-foreground w-10 text-right">
                              {batch.progress_percent}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={isOverdue ? 'destructive' : 'outline'}
                            onClick={() => handleMarkColonised(batch)}
                            disabled={isProcessing || mutationLoading}
                          >
                            {isProcessing ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Colonised'
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Spawn Batches Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Spawn
              {spawnBatches.length > 0 && (
                <span className="text-muted-foreground font-normal ml-2 text-sm">
                  {spawnBatches.length} {spawnBatches.length === 1 ? 'batch' : 'batches'} · {summary.total_spawn_bags || 0} bags
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {spawnBatches.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">
                No spawn colonising
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Inoculated</TableHead>
                    <TableHead>Batch Code</TableHead>
                    <TableHead>Expected Ready</TableHead>
                    <TableHead>Strain</TableHead>
                    <TableHead className="text-right">Bags</TableHead>
                    <TableHead className="text-right">Weight (kg)</TableHead>
                    <TableHead className="w-48">Progress</TableHead>
                    <TableHead className="w-32">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {spawnBatches.map((batch) => {
                    const isProcessing = processingIds.has(batch.id)
                    const isOverdue = batch.progress_percent >= 100

                    return (
                      <TableRow key={batch.id} className={isOverdue ? 'bg-destructive/5' : ''}>
                        <TableCell>{formatDate(batch.inoculation_date)}</TableCell>
                        <TableCell className="font-medium">{batch.batch_code}</TableCell>
                        <TableCell>
                          <span className={isOverdue ? 'text-destructive font-medium' : ''}>
                            {formatDate(batch.expected_end_date)}
                          </span>
                          {batch.days_remaining > 0 && (
                            <span className="text-muted-foreground text-sm ml-1">
                              ({batch.days_remaining}d)
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{batch.strain_code}</span>
                          <span className="text-muted-foreground ml-1">{batch.strain_name}</span>
                        </TableCell>
                        <TableCell className="text-right">{batch.total_blocks}</TableCell>
                        <TableCell className="text-right">
                          {batch.total_weight_kg ? batch.total_weight_kg.toFixed(1) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress
                              value={Math.min(batch.progress_percent, 100)}
                              className="h-2 flex-1"
                            />
                            <span className="text-xs text-muted-foreground w-10 text-right">
                              {batch.progress_percent}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={isOverdue ? 'destructive' : 'outline'}
                            onClick={() => handleMarkColonised(batch)}
                            disabled={isProcessing || mutationLoading}
                          >
                            {isProcessing ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Colonised'
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  )
}
